import { subYears } from 'date-fns/subYears';
import { guidFor } from '@ember/object/internals';
import { restartableTask } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import BaseRepositoryService from 'uplisting-frontend/services/repositories/-base';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import {
  startOfDay,
  endOfDay,
  formatDateQuery,
} from 'uplisting-frontend/utils/date';
import { computeDateRange } from 'uplisting-frontend/utils/airbnb-quality-dashboard';

export type AirbnbQualityDashboardGranularity = 'weekly' | 'monthly';

export const DEFAULT_INCLUDE = 'reviews,review_average,review_trends,listings';

export const QUALITY_STATS_INCLUDE =
  'quality_stat_months,quality_stat_listings';

export const QUALITY_STATS_LOOKBACK_MONTHS = 12;

export interface IDataForQueryParams {
  lookback: number;
  granularity?: AirbnbQualityDashboardGranularity;
  include?: string;
  countries?: string[];
  cities?: string[];
  client_names?: string[];
  airbnb_official_connection_ids?: string[];
  airbnb_official_link_ids?: number[];
  quality_statuses?: string[];
  cancellation_policies?: string[];
  trip_issues?: boolean;
}

export type IFilterQueryParams = Partial<
  Omit<IDataForQueryParams, 'lookback' | 'granularity' | 'include'>
>;

export interface ISectionSettings {
  lookback: number;
  granularity?: AirbnbQualityDashboardGranularity;
  include: string;
  afterFetch?: () => void | Promise<void>;
}

interface IQueryParams extends IDataForQueryParams {
  start_date: string;
  end_date: string;
  include: string;
}

export interface IDataCache {
  reviews: ModelRegistry['airbnb-official-review'][];
  reviewAverage: ModelRegistry['airbnb-quality-review-average'];
  reviewTrends: ModelRegistry['airbnb-quality-review-trend'][];
  listings: ModelRegistry['airbnb-quality-listing'][];
  qualityStatMonths: ModelRegistry['airbnb-quality-stat-month'][];
  qualityStatListings: ModelRegistry['airbnb-quality-stat-listing'][];
}

export interface IYoyQueryParams extends IFilterQueryParams {
  start_date: string;
  end_date: string;
  granularity: AirbnbQualityDashboardGranularity;
}

function computeParamsFor(params: IDataForQueryParams): IQueryParams {
  const dateRange = computeDateRange(params.lookback);

  return {
    ...params,
    include: params.include ?? DEFAULT_INCLUDE,
    start_date: dateRange.startDateStr,
    end_date: dateRange.endDateStr,
  };
}

export default class AirbnbQualityDashboardRepositoryService extends BaseRepositoryService<
  ModelRegistry['airbnb-quality-dashboard']
> {
  recordName = 'airbnb-quality-review-trend';
  implementMethods = [];

  defaultLookbackMonths = 6;
  defaultPageSize = 8;

  // Configurable threshold for "too strict" cancellation policy warning (default 70%)
  readonly strictPolicyThreshold = 0.7;

  @tracked dataCache: Record<string, IDataCache> = {};
  @tracked yoyCache: Record<
    string,
    ModelRegistry['airbnb-quality-review-trend'][]
  > = {};
  @tracked activeFilterParams: IFilterQueryParams = {};
  #currentFetchParams: IDataForQueryParams | null = null;
  #activeSections = new Map<string, ISectionSettings>();

  public fetchDataTask = restartableTask(
    async (params: IDataForQueryParams) => {
      this.#currentFetchParams = params;

      const queryParams = computeParamsFor(params);

      const cacheKey = this.#buildCacheKey(queryParams);

      if (this.dataCache[cacheKey]) {
        return;
      }

      try {
        const data = await this.store.queryRecord(
          'airbnb-quality-dashboard',
          queryParams,
        );

        if (!data) {
          return;
        }

        this.dataCache = {
          ...this.dataCache,
          [cacheKey]: {
            reviews: data.reviews?.slice() ?? [],
            reviewAverage: data.reviewAverage,
            reviewTrends: data.reviewTrends?.slice() ?? [],
            listings: data.listings?.slice() ?? [],
            qualityStatMonths: data.qualityStatMonths?.slice() ?? [],
            qualityStatListings: data.qualityStatListings?.slice() ?? [],
          },
        };
      } catch {
        this.notifications.error(
          'insights_airbnb_quality_dashboard.fetch_error',
        );
      } finally {
        this.#currentFetchParams = null;
      }
    },
  );

  public isLoadingFor(params: IDataForQueryParams): boolean {
    if (!this.fetchDataTask.isRunning) {
      return false;
    }

    if (!this.#currentFetchParams) {
      return false;
    }

    return (
      params.lookback === this.#currentFetchParams.lookback &&
      params.granularity === this.#currentFetchParams.granularity &&
      params.include === this.#currentFetchParams.include
    );
  }

  public getDataFor(params: IDataForQueryParams): IDataCache | undefined {
    const queryParams = computeParamsFor(params);
    const cacheKey = this.#buildCacheKey(queryParams);
    let cachedData = this.dataCache[cacheKey];

    if (!cachedData && params.include && params.include !== DEFAULT_INCLUDE) {
      const broaderParams = {
        ...params,
        include: DEFAULT_INCLUDE,
      };
      const broaderQueryParams = computeParamsFor(broaderParams);
      const broaderCacheKey = this.#buildCacheKey(broaderQueryParams);
      cachedData = this.dataCache[broaderCacheKey];
    }

    return cachedData;
  }

  public fetchYoyDataTask = restartableTask(async (params: IYoyQueryParams) => {
    const cacheKey = this.#buildYoyCacheKey(params);

    if (this.yoyCache[cacheKey]) {
      return;
    }

    try {
      const data = await this.store.queryRecord('airbnb-quality-dashboard', {
        ...params,
        include: 'review_trends',
      });

      if (!data) {
        return;
      }

      this.yoyCache = {
        ...this.yoyCache,
        [cacheKey]: data.reviewTrends?.slice() ?? [],
      };
    } catch {
      this.notifications.error('insights_airbnb_quality_dashboard.fetch_error');
    }
  });

  public getYoyDataFor(
    params: IYoyQueryParams,
  ): ModelRegistry['airbnb-quality-review-trend'][] | undefined {
    const cacheKey = this.#buildYoyCacheKey(params);
    return this.yoyCache[cacheKey];
  }

  public computeYoyDateRange(
    startDate: Date,
    endDate: Date,
  ): { startDateStr: string; endDateStr: string } {
    const yoyStartDate = startOfDay(subYears(startDate, 1));
    const yoyEndDate = endOfDay(subYears(endDate, 1));

    return {
      startDateStr: formatDateQuery(yoyStartDate),
      endDateStr: formatDateQuery(yoyEndDate),
    };
  }

  public async ensureDataFor(params: IDataForQueryParams): Promise<void> {
    const queryParams = computeParamsFor(params);
    const cacheKey = this.#buildCacheKey(queryParams);

    if (this.dataCache[cacheKey]) {
      return;
    }

    try {
      const data = await this.store.queryRecord(
        'airbnb-quality-dashboard',
        queryParams,
      );

      if (!data) {
        return;
      }

      this.dataCache = {
        ...this.dataCache,
        [cacheKey]: {
          reviews: data.reviews?.slice() ?? [],
          reviewAverage: data.reviewAverage,
          reviewTrends: data.reviewTrends?.slice() ?? [],
          listings: data.listings?.slice() ?? [],
          qualityStatMonths: data.qualityStatMonths?.slice() ?? [],
          qualityStatListings: data.qualityStatListings?.slice() ?? [],
        },
      };
    } catch {
      this.notifications.error('insights_airbnb_quality_dashboard.fetch_error');
    }
  }

  public registerSection(id: string, settings: ISectionSettings): void {
    this.#activeSections.set(id, settings);
  }

  public unregisterSection(id: string): void {
    this.#activeSections.delete(id);
  }

  public async refetchActiveSections(): Promise<void> {
    const promises: Promise<void>[] = [];

    for (const [, settings] of this.#activeSections) {
      const isCoveredByDefaultFetch =
        settings.lookback === this.defaultLookbackMonths &&
        (!settings.granularity || settings.granularity === 'monthly') &&
        settings.include
          .split(',')
          .every((part) => DEFAULT_INCLUDE.split(',').includes(part));

      if (isCoveredByDefaultFetch) {
        continue;
      }

      const params: IDataForQueryParams = {
        lookback: settings.lookback,
        granularity: settings.granularity,
        include: settings.include,
        ...this.activeFilterParams,
      };

      const promise = this.ensureDataFor(params).then(() =>
        settings.afterFetch?.(),
      );

      promises.push(promise);
    }

    await Promise.all(promises);
  }

  public resetCache(): void {
    this.dataCache = {};
    this.yoyCache = {};

    this.store.unloadAll('airbnb-quality-dashboard');
    this.store.unloadAll('airbnb-quality-review');
    this.store.unloadAll('airbnb-quality-review-average');
    this.store.unloadAll('airbnb-quality-stat-month');
    this.store.unloadAll('airbnb-quality-stat-listing');
    this.store.unloadAll(this.recordName);
  }

  public getPendingOpportunitiesForListing(
    listing: ModelRegistry['airbnb-quality-listing'],
    opportunities: ModelRegistry['airbnb-official-opportunity'][],
  ): number {
    if (!listing?.externalId || !opportunities?.length) {
      return 0;
    }

    return opportunities.filter((opportunity) => {
      const applicableIds = opportunity.applicableListingIds || [];
      return applicableIds.includes(listing.externalId);
    }).length;
  }

  #buildCacheKey(queryParams: IQueryParams): string {
    return guidFor(JSON.stringify(queryParams));
  }

  #buildYoyCacheKey(params: IYoyQueryParams): string {
    return guidFor(JSON.stringify(params));
  }
}

declare module '@ember/service' {
  interface Registry {
    'services/airbnb-quality-dashboard': AirbnbQualityDashboardRepositoryService;
  }
}
